import { useLanguage } from "@/lib/i18n";
import { Waves, ShieldCheck, Droplets, Settings, Thermometer, Wind, Zap, Shield, Gauge, Award, Wrench, Factory, Leaf, Recycle, Building2, CheckCircle } from "lucide-react";
import SEO from "@/components/SEO";

export default function EngineeringPage() {
  const { t, isRTL } = useLanguage();

  const features = [
    {
      icon: Waves,
      title: t("engineeringPage.premiumAcrylic"),
      text: t("engineeringPage.premiumAcrylicDesc")
    },
    {
      icon: ShieldCheck,
      title: t("engineeringPage.reinforcedBase"),
      text: t("engineeringPage.reinforcedBaseDesc")
    },
    {
      icon: Droplets,
      title: t("engineeringPage.advancedJets"),
      text: t("engineeringPage.advancedJetsDesc")
    },
    {
      icon: Settings,
      title: t("engineeringPage.ozoneFiltration"),
      text: t("engineeringPage.ozoneFiltrationDesc")
    },
    {
      icon: Thermometer,
      title: t("engineeringPage.balboaControl"),
      text: t("engineeringPage.balboaControlDesc")
    },
    {
      icon: Wind,
      title: t("engineeringPage.efficientPumps"),
      text: t("engineeringPage.efficientPumpsDesc")
    },
  ];

  const technicalSpecs = [
    {
      icon: Zap,
      title: t("engineeringPage.electrical"),
      specs: [
        t("engineeringPage.elecSpec1") || "Single-phase 220-240V / 50Hz",
        t("engineeringPage.elecSpec2") || "3-phase options available for commercial",
        t("engineeringPage.elecSpec3") || "GFCI protection standard",
        t("engineeringPage.elecSpec4") || "Power consumption: 1-3 kW typical"
      ]
    },
    {
      icon: Gauge,
      title: t("engineeringPage.hydraulic"),
      specs: [
        t("engineeringPage.hydSpec1") || "Flow rate: 150-400 LPM",
        t("engineeringPage.hydSpec2") || "Pump capacity: 2-6 HP",
        t("engineeringPage.hydSpec3") || "Jet pressure: 0.5-2.0 bar adjustable",
        t("engineeringPage.hydSpec4") || "Heating rate: 1-2°C per hour"
      ]
    },
    {
      icon: Shield,
      title: t("engineeringPage.safety"),
      specs: [
        t("engineeringPage.safeSpec1") || "CE certified components",
        t("engineeringPage.safeSpec2") || "IP65 rated electrical enclosures",
        t("engineeringPage.safeSpec3") || "Anti-entrapment drain covers",
        t("engineeringPage.safeSpec4") || "Emergency shut-off systems"
      ]
    },
    {
      icon: Wrench,
      title: t("engineeringPage.installReq"),
      specs: [
        t("engineeringPage.instSpec1") || "Level surface (±5mm tolerance)",
        t("engineeringPage.instSpec2") || "Load-bearing capacity: 400+ kg/m²",
        t("engineeringPage.instSpec3") || "Drainage access point",
        t("engineeringPage.instSpec4") || "Electrical isolation switch"
      ]
    }
  ];

  const manufacturingProcess = [
    {
      step: "01",
      title: t("engineeringPage.step1"),
      description: t("engineeringPage.step1Desc")
    },
    {
      step: "02",
      title: t("engineeringPage.step2"),
      description: t("engineeringPage.step2Desc")
    },
    {
      step: "03",
      title: t("engineeringPage.step3"),
      description: t("engineeringPage.step3Desc")
    },
    {
      step: "04",
      title: t("engineeringPage.step4"),
      description: t("engineeringPage.step4Desc")
    }
  ];

  return (
    <div data-testid="engineering-page">
      <SEO 
        title="Engineering Excellence"
        description="Discover the advanced engineering behind Pruva Jacuzzi systems. Premium American acrylic, 304 stainless steel construction, Balboa controls, and energy-efficient hydrotherapy technology for Dubai's luxury wellness market."
        keywords="jacuzzi engineering, spa technology, hydrotherapy systems, balboa controls, acrylic spa, stainless steel jacuzzi, energy efficient hot tub, spa filtration, ozone sterilization, dubai spa manufacturer"
        canonical={typeof window !== 'undefined' ? `${window.location.origin}/engineering` : "https://www.pruvajacuzzi.com/engineering"}
        breadcrumbs={[
          { name: "Home", url: typeof window !== 'undefined' ? window.location.origin : "https://www.pruvajacuzzi.com" },
          { name: "Engineering", url: typeof window !== 'undefined' ? `${window.location.origin}/engineering` : "https://www.pruvajacuzzi.com/engineering" }
        ]}
      />
      
      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20" style={{ background: "linear-gradient(135deg, #F8F9FA 0%, #F1F3F5 100%)" }}>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-amber-700 font-medium mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
                {t("engineering.subtitle")}
              </p>
              <h1 data-testid="engineering-title" className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 mb-6">
                {t("engineering.title")}
              </h1>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
                {t("engineeringPage.heroDesc1")}
              </p>
              <p className="text-base text-slate-500 leading-relaxed mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
                {t("engineeringPage.heroDesc2")}
              </p>
              <div className={`flex flex-wrap gap-6 text-sm text-slate-600 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Award className="w-5 h-5 text-amber-700" />
                  <span>{t("engineeringPage.ceCertified")}</span>
                </div>
                <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Factory className="w-5 h-5 text-amber-700" />
                  <span>{t("engineeringPage.madeInTurkey")}</span>
                </div>
                <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Shield className="w-5 h-5 text-amber-700" />
                  <span>{t("engineeringPage.warranty")}</span>
                </div>
              </div>
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src="/images/optimized/engineering.webp"
                alt="Pruva Jacuzzi engineering and manufacturing"
                loading="lazy"
                className="w-full h-full object-cover object-bottom"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Technologies */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-amber-700 font-medium mb-3">{t("engineeringPage.coreTech")}</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900">
              {t("engineeringPage.coreTechSubtitle")}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {features.map((feat, i) => (
              <div key={feat.title} data-testid={`eng-feature-${i}`}
                className="p-7 bg-white border border-slate-100 hover:border-slate-300 hover:shadow-md transition-all duration-300">
                <div className="w-10 h-10 flex items-center justify-center bg-slate-100 mb-5">
                  <feat.icon className="w-5 h-5 text-slate-800" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2.5" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {feat.title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {feat.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-16 md:py-24" style={{ background: "linear-gradient(135deg, #F8F9FA 0%, #F1F3F5 100%)" }}>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-amber-700 font-medium mb-3">{t("engineeringPage.techSpecs")}</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900">
              {t("engineeringPage.techSpecsSubtitle")}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {technicalSpecs.map((spec) => (
              <div key={spec.title} className="bg-white p-6 border border-slate-100">
                <div className={`flex items-center gap-3 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <spec.icon className="w-5 h-5 text-amber-700" strokeWidth={1.5} />
                  <h3 className="text-base font-medium text-slate-900">{spec.title}</h3>
                </div>
                <ul className="space-y-2">
                  {spec.specs.map((item, j) => (
                    <li key={j} className={`text-sm text-slate-600 flex items-start gap-2 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                      <span className="w-1 h-1 bg-slate-400 rounded-full mt-2 flex-shrink-0"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sustainable Filtration Design - LEED Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-emerald-900 via-emerald-800 to-green-900 text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className={`flex items-center gap-3 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Leaf className="w-6 h-6 text-green-400" />
                </div>
                <p className="text-sm uppercase tracking-widest text-green-400 font-medium">{t("engineeringPage.sustainableEng") || "Sustainable Engineering"}</p>
              </div>
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight mb-6">
                {t("engineeringPage.filtrationTitle") || "Pruva Filtration Design: Water Conservation Leadership"}
              </h2>
              <p className="text-lg text-emerald-100 leading-relaxed mb-6">
                {t("engineeringPage.filtrationDesc1") || "Our advanced filtration systems are engineered for maximum efficiency with minimal environmental impact. The Pruva filtration design dramatically reduces water waste through optimized backwash cycles and intelligent flow management."}
              </p>
              <p className="text-base text-emerald-200/80 leading-relaxed mb-8">
                {t("engineeringPage.filtrationDesc2") || "Traditional spa filtration systems require frequent backwashing that wastes thousands of liters annually. Pruva's engineering team has developed a proprietary filtration approach that maintains crystal-clear water quality while significantly reducing discharge volumes."}
              </p>
              
              {/* 70% Highlight Box */}
              <div className="bg-white/10 backdrop-blur-sm border border-green-400/30 p-6 mb-8">
                <div className={`flex items-center gap-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="text-center">
                    <div className="text-5xl md:text-6xl font-bold text-green-400">70%</div>
                    <div className="text-sm text-emerald-200 mt-1">{t("engineeringPage.lessBackwash") || "Less Backwash"}</div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-white mb-2">{t("engineeringPage.reducedDischarge") || "Reduced Water Discharge"}</h3>
                    <p className="text-sm text-emerald-200/80">
                      {t("engineeringPage.reducedDischargeDesc") || "Pruva systems require up to 70% less backwash water discharge compared to conventional filtration systems, saving thousands of liters per year."}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {[
                  t("engineeringPage.envBenefit1") || "High-efficiency filter media with extended service life",
                  t("engineeringPage.envBenefit2") || "Optimized backwash cycles triggered by actual system needs",
                  t("engineeringPage.envBenefit3") || "Reduced chemical consumption through superior filtration",
                  t("engineeringPage.envBenefit4") || "Lower operating costs and maintenance requirements"
                ].map((item) => (
                  <div key={item} className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-emerald-100">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* LEED Certification Connection */}
            <div className="space-y-6">
              <div className="bg-white/10 backdrop-blur-sm border border-green-400/30 p-8">
                <div className={`flex items-center gap-4 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Building2 className="w-8 h-8 text-green-400" />
                  <h3 className="text-xl font-medium">{t("engineeringPage.leedTitle") || "LEED Certification Support"}</h3>
                </div>
                <p className="text-emerald-100 leading-relaxed mb-6">
                  {t("engineeringPage.leedDesc") || "For developers pursuing LEED certification, Pruva's water-efficient spa systems contribute directly to achieving Water Efficiency credits. Our reduced backwash technology supports sustainable building goals without compromising on luxury or performance."}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-900/50 p-4 text-center">
                    <div className="text-2xl font-semibold text-green-400 mb-1">WE</div>
                    <div className="text-xs text-emerald-200">{t("engineeringPage.waterEfficiency") || "Water Efficiency Credits"}</div>
                  </div>
                  <div className="bg-green-900/50 p-4 text-center">
                    <div className="text-2xl font-semibold text-green-400 mb-1">EA</div>
                    <div className="text-xs text-emerald-200">{t("engineeringPage.energyAtmosphere") || "Energy & Atmosphere"}</div>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm border border-green-400/30 p-8">
                <div className={`flex items-center gap-4 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Recycle className="w-6 h-6 text-green-400" />
                  <h3 className="text-lg font-medium">{t("engineeringPage.envImpact") || "Environmental Impact"}</h3>
                </div>
                <div className="space-y-4">
                  <div className={`flex justify-between items-center border-b border-green-400/20 pb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span className="text-sm text-emerald-200">{t("engineeringPage.annualSavings") || "Annual Water Savings"}</span>
                    <span className="text-green-400 font-medium">{t("engineeringPage.annualSavingsVal") || "Up to 15,000 L"}</span>
                  </div>
                  <div className={`flex justify-between items-center border-b border-green-400/20 pb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span className="text-sm text-emerald-200">{t("engineeringPage.chemicalReduction") || "Chemical Reduction"}</span>
                    <span className="text-green-400 font-medium">{t("engineeringPage.chemicalReductionVal") || "40% Less"}</span>
                  </div>
                  <div className={`flex justify-between items-center border-b border-green-400/20 pb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span className="text-sm text-emerald-200">{t("engineeringPage.filterLife") || "Filter Life Extension"}</span>
                    <span className="text-green-400 font-medium">{t("engineeringPage.filterLifeVal") || "2x Longer"}</span>
                  </div>
                  <div className={`flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span className="text-sm text-emerald-200">{t("engineeringPage.carbonFootprint") || "Carbon Footprint"}</span>
                    <span className="text-green-400 font-medium">{t("engineeringPage.carbonFootprintVal") || "Significantly Reduced"}</span>
                  </div>
                </div>
              </div>

              <p className="text-sm text-emerald-300/70 italic text-center">
                {t("engineeringPage.sustainableQuote") || "\"Pruva's commitment to sustainable engineering makes our systems the preferred choice for environmentally conscious developments across the UAE and beyond.\""}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Manufacturing Process */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-amber-700 font-medium mb-3">{t("engineeringPage.mfgProcess")}</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900">
              {t("engineeringPage.mfgProcessSubtitle")}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {manufacturingProcess.map((process) => (
              <div key={process.step} className="relative">
                <div className="text-6xl font-bold text-slate-100 mb-4">{process.step}</div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">{process.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Assurance */}
      <section className="py-16 md:py-20 bg-slate-800 text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-amber-400 font-medium mb-3">{t("engineeringPage.qaTitle")}</p>
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight mb-6">
                {t("engineeringPage.qaSubtitle") || "Every System Tested, Every Detail Verified"}
              </h2>
              <p className="text-slate-300 leading-relaxed mb-6">
                {t("engineeringPage.qaText")}
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="text-3xl font-semibold text-amber-400 mb-1">100%</div>
                  <div className="text-sm text-slate-400">{t("engineeringPage.pressureTested") || "Pressure Tested"}</div>
                </div>
                <div>
                  <div className="text-3xl font-semibold text-amber-400 mb-1">48hr</div>
                  <div className="text-sm text-slate-400">{t("engineeringPage.waterTestPeriod") || "Water Test Period"}</div>
                </div>
                <div>
                  <div className="text-3xl font-semibold text-amber-400 mb-1">116</div>
                  <div className="text-sm text-slate-400">{t("engineeringPage.qualityCheckpoints") || "Quality Checkpoints"}</div>
                </div>
                <div>
                  <div className="text-3xl font-semibold text-amber-400 mb-1">10yr</div>
                  <div className="text-sm text-slate-400">{t("engineeringPage.structuralWarranty") || "Structural Warranty"}</div>
                </div>
              </div>
            </div>
            <div className="bg-slate-700/50 p-8 border border-slate-600">
              <h3 className="text-lg font-medium mb-4">{t("engineeringPage.qaCommitments") || "Our Quality Commitments"}</h3>
              <ul className="space-y-3 text-sm text-slate-300">
                <li className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                  <ShieldCheck className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <span>{t("engineeringPage.qaCommit1") || "Every component sourced from certified international suppliers"}</span>
                </li>
                <li className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                  <ShieldCheck className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <span>{t("engineeringPage.qaCommit2") || "Full traceability from raw materials to finished product"}</span>
                </li>
                <li className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                  <ShieldCheck className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <span>{t("engineeringPage.qaCommit3") || "Independent third-party testing and certification"}</span>
                </li>
                <li className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                  <ShieldCheck className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <span>{t("engineeringPage.qaCommit4") || "Dedicated after-sales technical support team in UAE"}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
